/**
 * gift-discovery.js — Snowcubes "Gift help?" discovery widget (Phase 2.3).
 *
 * Quiet Kitchen: 2D flat, paper/ink, no box-shadow, no hover lift.
 * Behind feature flag `sc_gift_help_enabled` (default off, ?sc_gift_help_enabled=1).
 *
 * Rendered only on homepage (`index`) and collection templates via Liquid in
 * theme.liquid. This component owns the trigger + drawer shell + the
 * /api/gift-suggest call. The Worker (Phase 2.2) is not yet deployed, so the
 * fetch is graceful: an absent/erroring endpoint shows a calm offline state and
 * never throws into the shopper console.
 *
 * Pure logic is exposed on `exports.logic` for unit testing; the custom element
 * is defined on the DOM only when `customElements` is available.
 */
(function (global, factory) {
  'use strict';

  var logic = {
    GIFT_SUGGEST_ENDPOINT: '/api/gift-suggest',
    FIRST_PROMPT: "Who's this for?",
    TRIGGER_LABEL: 'Gift help?',
    DRAWER_HEADER: 'Gift help',

    shouldRender: function (flagEnabled, pageType) {
      if (!flagEnabled) return false;
      return pageType === 'index' || pageType === 'collection';
    },

    parsePicks: function (payload) {
      if (!payload || typeof payload !== 'object') return [];
      var picks = Array.isArray(payload.picks) ? payload.picks : [];
      return picks
        .map(function (p) {
          if (!p || typeof p !== 'object') return null;
          var gid = typeof p.variantId === 'string' ? p.variantId : null;
          return {
            title: typeof p.title === 'string' ? p.title : '',
            blurb: typeof p.blurb === 'string' ? p.blurb : '',
            variantId: gid,
            handle: typeof p.handle === 'string' ? p.handle : '',
          };
        })
        .filter(function (p) {
          return p && p.title && p.variantId;
        });
    },

    offlineState: function () {
      return { ok: false, reason: 'worker_unavailable' };
    },

    // Phase 2.4 — /cart/add.js payload. Shopify AJAX wants the numeric variant
    // id (last segment of the GID). NEVER use MCP update_cart from the storefront
    // (snowai rule — it creates a ghost cart the shopper can't see).
    variantIdForCart: function (gid) {
      if (typeof gid !== 'string' || !gid) return null;
      var parts = gid.split('/');
      var last = parts[parts.length - 1];
      return /^[0-9]+$/.test(last) ? last : null;
    },

    buildCartPayload: function (variantIds, quantity) {
      var qty = quantity || 1;
      var self = this;
      var items = (Array.isArray(variantIds) ? variantIds : [variantIds])
        .map(function (v) { return self.variantIdForCart(v); })
        .filter(function (id) { return id !== null; })
        .map(function (id) { return { id: id, quantity: qty }; });
      return { items: items };
    },

    // Phase 2.5 — Operation Santa handoff. Use a DISTINCT key, NOT the serialized
    // gift-config key (sc_gift_config_prototype) that gift-card-core owns — stashing
    // a {handle,title} stub there would corrupt the real gift config on that product
    // (gift-card-core's config-load would read this non-config and break the card).
    // The actual modal handoff is the ?sc_gift_ui=1 param; this is just a pick hint.
    giftConfigKey: function (handle) {
      return 'sc_gift_discovery_pick:' + handle;
    },

    // Escape values from the (LLM-backed, semi-untrusted) gift-suggest response
    // before they're injected into the drawer innerHTML — a prompt-injected or
    // MITM'd title/blurb/variantId must not become markup/script in the storefront.
    escapeHtml: function (s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    },
  };

  function defineElement() {
    if (typeof customElements === 'undefined' || customElements.get('gift-discovery')) return;

    class GiftDiscovery extends HTMLElement {
      _track(eventKey, properties) {
        var analytics = global.SnowcubesAnalytics;
        var eventName = analytics && analytics.EVENTS && analytics.EVENTS[eventKey];
        if (!eventName || typeof analytics?.track !== 'function') return;
        analytics.track(eventName, properties || {});
      }

      connectedCallback() {
        var Flags = global.SnowcubesFlags;
        var enabled = !!(Flags && typeof Flags.isEnabled === 'function' && Flags.isEnabled('sc_gift_help_enabled'));
        var pageType = this.getAttribute('data-page-type') || '';
        if (!logic.shouldRender(enabled, pageType)) {
          this.remove();
          return;
        }
        this._open = false;
        this._render();
      }

      _render() {
        this.innerHTML =
          '<button type="button" class="sc-gift-help__trigger" data-gift-help-toggle aria-expanded="false" aria-haspopup="dialog">' +
          '<span>' + logic.TRIGGER_LABEL + '</span>' +
          '</button>' +
          '<section class="sc-gift-help__drawer" data-gift-help-drawer hidden role="dialog" aria-modal="true" aria-label="' + logic.DRAWER_HEADER + '">' +
            '<header class="sc-gift-help__head"><span>' + logic.DRAWER_HEADER + '</span>' +
              '<button type="button" class="sc-gift-help__close" data-gift-help-close aria-label="Close">&times;</button>' +
            '</header>' +
            '<div class="sc-gift-help__body" data-gift-help-body>' +
              '<p class="sc-gift-help__prompt">' + logic.FIRST_PROMPT + '</p>' +
              '<p class="sc-gift-help__quiet">Looking for a few gift options.</p>' +
            '</div>' +
          '</section>';

        this.querySelector('[data-gift-help-toggle]').addEventListener('click', this._onToggle.bind(this));
        this.querySelector('[data-gift-help-close]').addEventListener('click', this._close.bind(this));
      }

      async _onToggle() {
        if (this._open) return this._close();
        this._open = true;
        var drawer = this.querySelector('[data-gift-help-drawer]');
        var toggle = this.querySelector('[data-gift-help-toggle]');
        if (drawer) drawer.hidden = false;
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
        this._track('GIFT_HELP_OPENED', {
          gift_surface: this.getAttribute('data-page-type') || 'unknown',
        });
        await this._ask();
      }

      _close() {
        this._open = false;
        var drawer = this.querySelector('[data-gift-help-drawer]');
        var toggle = this.querySelector('[data-gift-help-toggle]');
        if (drawer) drawer.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }

      async _ask() {
        var body = this.querySelector('[data-gift-help-body]');
        if (!body) return;
        try {
          var res = await fetch(logic.GIFT_SUGGEST_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: logic.FIRST_PROMPT }),
          });
          if (!res.ok) throw new Error('non-ok');
          var data = await res.json();
          var picks = logic.parsePicks(data);
          this._track('GIFT_HELP_RESULT', {
            result: picks.length ? 'success' : 'empty',
            pick_count: picks.length,
          });
          this._renderPicks(body, picks);
        } catch (_) {
          this._track('GIFT_HELP_RESULT', { result: 'unavailable', pick_count: 0 });
          body.innerHTML =
            '<p class="sc-gift-help__prompt">' + logic.FIRST_PROMPT + '</p>' +
            '<p class="sc-gift-help__quiet sc-gift-help__offline">Gift help is unavailable. <a href="/collections/snowcubes">Browse Snowcubes</a>.</p>';
        }
      }

      _renderPicks(body, picks) {
        if (!picks.length) {
          body.innerHTML =
            '<p class="sc-gift-help__prompt">' + logic.FIRST_PROMPT + '</p>' +
            '<p class="sc-gift-help__quiet">No picks yet. <a href="/collections/snowcubes">Browse Snowcubes</a> to choose a box yourself.</p>';
          return;
        }
        var html = picks.map(function (p) {
          return '<button type="button" class="sc-gift-help__pick" data-variant="' + logic.escapeHtml(p.variantId) + '">' +
            '<span class="sc-gift-help__pick-title">' + logic.escapeHtml(p.title) + '</span>' +
            (p.blurb ? '<span class="sc-gift-help__pick-blurb">' + logic.escapeHtml(p.blurb) + '</span>' : '') +
            '</button>';
        }).join('');
        body.innerHTML =
          '<p class="sc-gift-help__prompt">A few ideas.</p>' +
          '<div class="sc-gift-help__picks">' + html + '</div>';
        var picksEls = body.querySelectorAll('.sc-gift-help__pick');
        var self = this;
        picksEls.forEach(function (el) {
          var pick = picks.filter(function (p) { return p.variantId === el.getAttribute('data-variant'); })[0];
          el.addEventListener('click', function () { self._onPickClick(pick); });
        });
      }

      _onPickClick(pick) {
        if (!pick || !pick.variantId) return;
        var payload = logic.buildCartPayload([pick.variantId], 1);
        if (!payload.items.length) return;
        this._track('GIFT_HELP_PICK_SELECTED', {
          has_product_handle: !!pick.handle,
        });
        // Operation Santa prefill: stash the pick so the PDP gift modal can pick it up.
        if (pick.handle && global.localStorage) {
          try { global.localStorage.setItem(logic.giftConfigKey(pick.handle), JSON.stringify({ handle: pick.handle, title: pick.title })); } catch (_) {}
        }
        // /cart/add.js (Shopify AJAX). Hard-refresh-free: let the theme's own cart drawer react.
        var self = this;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).then(function (r) {
          if (!r.ok) {
            self._track('GIFT_HELP_ADD_RESULT', { result: 'failed', failure_class: 'http_error' });
            return;
          }
          self._track('GIFT_HELP_ADD_RESULT', { result: 'success' });
          if (pick.handle) { global.location.href = '/products/' + pick.handle + '?sc_gift_ui=1'; }
        }).catch(function () {
          self._track('GIFT_HELP_ADD_RESULT', { result: 'failed', failure_class: 'network_error' });
        });
      }
    }

    customElements.define('gift-discovery', GiftDiscovery);
  }

  defineElement();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { logic: logic };
  } else {
    global.SnowcubesGiftDiscovery = { logic: logic };
  }
})(typeof globalThis !== 'undefined' ? globalThis : typeof self !== 'undefined' ? self : this);
